// eslint-disable-next-line max-classes-per-file
const q = require('queried');
const ext = require('deep-extend');
const _ = require('../i18n');

const {
  Controller,
  View,
} = require('./core');

const {
  Filter,
} = require('./filter');

/**
 * Get facet filter state from server-rendered DOM nodes.
 *
 * @param element
 * @returns {*}
 */
function getInitialState(element) {
  const attrKeys = [
    'data-key',
    'data-value',
    'data-in-url',
  ];

  const activeContainers = q.all('.active', element);
  const activeLinks = activeContainers.map((c) => q('a[data-key]', c) || c);
  const filterAttributes = activeLinks.map((el) => attrKeys.map((a) => el.getAttribute(a)));

  // this one is the only set filter value, so it's worth checking if it's our
  // urlFacetKey (the one that lives in main url not as a get param)
  let urlFacetKeyCandidate = null;

  const filtersReducer = (result, [key, value]) => {
    result[key] = result[key] || [];
    if (value) {
      urlFacetKeyCandidate = value;
      result[key].push(value);
    }
    return result;
  };
  const facetFilter = filterAttributes.reduce(filtersReducer, {});

  const facetsReducer = (result, params) => {
    const key = params[0];
    const value = params[1];
    const useInUrl = params[2];

    if (value === urlFacetKeyCandidate && useInUrl) {
      return key;
    }

    return result;
  };
  const urlFacetKey = filterAttributes.reduce(facetsReducer, null);
  const objCount = element.getAttribute('data-obj-count');
  const itemReadableName = element.getAttribute('data-readable-name');
  const state = {};

  state.facetFilter = facetFilter;
  if (urlFacetKey) {
    state.page = { urlFacetKey };
  }

  if (itemReadableName) {
    state.itemReadableName = itemReadableName;
  }

  if (objCount !== null) {
    state.objCount = objCount;
  }

  return state;
}

class FacetController extends Controller {
  constructor(...args) {
    super(...args);

    const initialState = getInitialState(this.view.element);

    this.model.update((state) => ext(state, initialState));
  }

  reset(key) {
    this.model.updateWithSync((state) => {
      if (state.facetFilter) {
        delete state.facetFilter[key];
      }

      return state;
    });
  }

  /**
   * Set single value and sync.
   *
   * Call reset if selected value is empty string,
   * because facet filter don't have [data-mode="reset"].
   *
   * @param key
   * @param value
   * @param keyInUrlPath
   */
  enable(key, value, keyInUrlPath = false) {
    if (value) {
      this.model.updateWithSync((state) => ext(state, {
        page: {
          urlFacetKey: keyInUrlPath ? key : state.page.urlFacetKey,
        },
        facetFilter: {
          [key]: [value],
        },
      }));
    } else {
      this.reset(key);
    }
  }

  toggle(key, value, keyGroup, keyInUrlPath = false) {
    this.model.updateWithSync((state) => {
      let values = (state.facetFilter && state.facetFilter[key]) || [];

      // reset concurrent mutual exclusive filters
      // you can choose only ony db-driven filter at a time and they're labeled
      // with 'option_slug' key_group data-attribute now, so reset state for
      // all of such items once one of them is being toggled
      if (keyGroup === 'option_slug') {
        q.all('[data-key_group="option_slug"]', this.element).forEach(
          (x) => delete state.facetFilter[x.getAttribute(['data-key'])],
        );
      }

      if (values.indexOf(value) === -1) {
        values = [value];
      } else {
        values.splice(values.indexOf(value), 1);
      }

      return ext(state, {
        page: {
          urlFacetKey: keyInUrlPath ? key : state.page.urlFacetKey,
        },
        facetFilter: {
          [key]: values.filter((x) => x.length),
        },
      });
    });
  }

  sync(data, state) {
    if (data.facets) {
      const facetKeys = Object.getOwnPropertyNames(data.facets);
      const facetEmptyIndicators = Array.prototype.concat.apply([], facetKeys.map((k) => {
        const values = data.facets[k];
        return values.map((v) => ({
          key: k,
          value: v.value_repr ? v.value_repr : '',
          empty: v.empty,
        }));
      }));

      const objCount = data.obj_count || '';
      const itemReadableName = data.item_readable_name || '';
      return ext(state, { facetEmptyIndicators, objCount, itemReadableName });
    }

    return state;
  }

  clear() {
    this.model.updateWithSync((state) => {
      state.facetFilter = {};
      return state;
    });
  }

  clearFilters() {
    this.model.updateWithSync((state) => {
      state.facetFilter = {};
      state.navigation = {};
      return state;
    });
  }
}

class VoidFacetController extends FacetController {
  bindHandlers() {}
}

/**
 * Sidebar mix filters
 * @class
 */
class FacetFilter extends Filter {
  /**
   * Reset all checkboxes, then set active only,
   * then update counters.
   *
   * @param state
   */
  render(state) {
    if (!state.facetFilter) return;

    q.all('.facet-item', this.element).forEach((x) => x.classList.remove('active'));
    q.all('.facet-item-checkbox', this.element).forEach((x) => x.classList.remove('checked'));

    const data = state.facetFilter;
    const keys = Object.getOwnPropertyNames(data);
    const pairPartitions = keys.map((k) => data[k].map((v) => [k, v]));
    const pairs = flatten(pairPartitions);
    const elemPartitions = pairs
      .map(([k, v]) => q.all(`[data-key="${k}"][data-value="${v}"]`, this.element));

    let elems = flatten(elemPartitions);
    if (!elems.length) {
      elems = q.all('[data-value=""]', this.element);
    }

    elems.forEach((elem) => {
      const mode = elem.getAttribute('data-mode');

      if (mode == 'checkbox') {
        elem.classList.add('checked');
      }

      elem.closest('.facet-item').classList.add('active');
    });

    function flatten(arr) {
      return Array.prototype.concat.apply([], arr);
    }
  }
}

class VoidFacetFilter extends FacetFilter {
  emitFor() {}
}

/**
 * Flat mix filters
 * They hide categories not containing any items
 * @class
 */
class FlatFacetFilter extends FacetFilter {
  render(state) {
    super.render(state);

    this.element.removeAttribute('hidden');

    if (state.facetEmptyIndicators) {
      this.hideEmptyOptions(state.facetEmptyIndicators);
    }
  }

  hideEmptyOptions(emptyIndicators) {
    emptyIndicators.forEach((c) => {
      const selector = `.facet-item:has(.facet-item-link[data-key="${c.key}"][data-value="${c.value}"])`;
      const item = q(selector, this.element);
      if (!item) return;

      const isActive = item.classList.contains('active');

      if (!isActive && c.empty) {
        item.classList.add('empty');
      } else {
        item.classList.remove('empty');
      }
    });
  }
}

class TagsFacetFilter extends FlatFacetFilter {
  constructor(...args) {
    super(...args);
    this.events.bind({
      'click .facet-tags-clear': (e) => this.emitFor('clear', e),
    });
  }

  render(state) {
    super.render(state);

    const facets = Object.keys(state.facetFilter);
    const parentElement = this.element.closest('.facet-tags');
    if (facets.some((x) => state.facetFilter[x].length)) {
      parentElement.removeAttribute('hidden');
    } else {
      parentElement.setAttribute('hidden', true);
    }
  }
}

const FILTER_FIELDS = ['facetFilter', 'navigation'];

/**
 * This method is born a bit unreadable
 * There are tests for it; you'd better read them,
 * because mostly all the possible cases are covered with them
 * @param  {Object} state - global feed state
 * @return {Boolean}      - result of assessment of state
 *                          answering the question: "Are filters empty?"
 */
function areFiltersEmpty(state) {
  return FILTER_FIELDS.every((fl) => {
    const field = state[fl];

    if (!field) {
      return true;
    }

    const fieldKeys = Object.keys(field);
    if (!fieldKeys.length) {
      return true;
    }

    if (fieldKeys.every((key) => !(field[key] && field[key].length))) {
      return true;
    }

    return false;
  });
}

class FacetFilterClear extends View {
  constructor(...args) {
    super(...args);
    this.events.bind({
      'click .clear-filters': () => this.emit('clearFilters'),
    });
  }

  render(state) {
    const itemsCount = q('.feed-items-count', this.element);
    const count = Number(state.objCount);
    const name = state.itemReadableName || '';

    const filtersEmpty = areFiltersEmpty(state);

    q.all('.hide-wo-filters').forEach((el) => {
      if (filtersEmpty) {
        el.setAttribute('hidden', true);
      } else {
        el.removeAttribute('hidden');
      }
    });

    if (count === 0) {
      itemsCount.textContent = _('Nothing was found for the chosen filters. ');
    } else {
      itemsCount.textContent = _.interpolate(_('%s %s found. '), [count, name]);
    }
  }
}

module.exports = {
  FacetController,
  FacetFilter,
  FlatFacetFilter,
  TagsFacetFilter,
  FacetFilterClear,
  VoidFacetController,
  VoidFacetFilter,
  areFiltersEmpty,
};
